import {ApolloLink, Observable} from '@apollo/client';
import {Kind} from 'graphql';
import {isMutation} from 'src/apolloCache/previews';
import type {
  ApolloCache,
  FetchResult,
  FieldPolicy,
  Reference,
  StoreObject,
} from '@apollo/client';
import type {DocumentNode} from 'graphql';

// suggestCategorizations answers per row, but Apollo would key it by the
// whole id list — so the create sheet asking about one row missed the answer
// the Inbox card had just fetched for thirteen. Stored per row instead, with
// every asked row recorded (a row with no match answers []: "no match" must
// not read as "never asked"), so any request for rows already answered is a
// cache hit, and one naming an unasked row goes to the network.
type SuggestionItem = StoreObject | Reference;
type SuggestionStore = {rows: Record<string, SuggestionItem[]>};

const rawTransactionIdsOf = (args: Record<string, unknown> | null): string[] => {
  const ids = args?.rawTransactionIds;
  return Array.isArray(ids)
    ? ids.filter((id: unknown): id is string => typeof id === 'string')
    : [];
};

export const suggestionsByRow: FieldPolicy<SuggestionStore, SuggestionItem[], SuggestionItem[]> = {
  keyArgs: false,
  merge: (existing, incoming, {args, readField}) => {
    const rows = {...existing?.rows};
    for (const id of rawTransactionIdsOf(args)) rows[id] = [];
    for (const suggestion of incoming) {
      const id = readField<string>('rawTransactionId', suggestion);
      if (id != null) rows[id] = [...(rows[id] ?? []), suggestion];
    }
    return {rows};
  },
  read: (existing, {args}) => {
    const ids = rawTransactionIdsOf(args);
    if (existing == null || ids.some((id) => !(id in existing.rows))) {
      return undefined;
    }
    return ids.flatMap((id) => existing.rows[id]);
  },
};

const selectsSuggestions = (query: DocumentNode): boolean =>
  query.definitions.some(
    (definition) =>
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.selectionSet.selections.some(
        (selection) =>
          selection.kind === Kind.FIELD &&
          selection.name.value === 'suggestCategorizations',
      ),
  );

/**
 * Keeps cached guesses no older than the last write.
 *
 * Every mutation drops them: guesses come from categorization history,
 * which nearly any write can move — categorizing another row from the same
 * merchant, editing or resetting a transaction, renaming a category — and
 * per-row storage means one answer would otherwise serve a sheet opened long
 * after it was fetched. Wired for all mutations rather than per call site
 * (see previews.ts for why). An open Inbox card refetches.
 *
 * And a guess request still in flight when a write completes may have read
 * history from before it; its answer would put the dropped guesses straight
 * back. So such an answer is never delivered: the request is sent again,
 * until one completes with no write landing meanwhile.
 */
export const createSuggestionInvalidationLink = (
  cache: ApolloCache<object>,
): ApolloLink => {
  let writes = 0;
  return new ApolloLink((operation, forward) => {
    if (isMutation(operation.query)) {
      return forward(operation).map((result) => {
        writes++;
        cache.evict({id: 'ROOT_QUERY', fieldName: 'suggestCategorizations'});
        cache.gc();
        return result;
      });
    }
    if (!selectsSuggestions(operation.query)) return forward(operation);
    const fetchFresh = (): Observable<FetchResult> => {
      const writesAtStart = writes;
      return forward(operation).flatMap((result) =>
        writes === writesAtStart ? Observable.of(result) : fetchFresh(),
      );
    };
    return fetchFresh();
  });
};
